import React, { useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { celebrateBadges } from "../lib/gamification";
import { ScanText, Loader2 } from "lucide-react";

export default function Breakdown() {
  const [prompt, setPrompt] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const onAnalyze = async () => {
    if (!prompt.trim()) { toast.error("Paste a prompt first."); return; }
    setLoading(true); setResult(null);
    try {
      const { data } = await api.post("/coach/breakdown", { prompt });
      setResult(data);
      celebrateBadges(data._meta);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not analyze");
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-8">
      <div>
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-2xl bg-indigo-100 text-indigo-700 grid place-items-center"><ScanText size={20}/></div>
          <h1 className="text-3xl font-bold" style={{fontFamily:'Outfit'}}>Prompt Breakdown</h1>
        </div>
        <p className="text-slate-600">Paste any writing prompt and I'll help you decode exactly what it's asking.</p>
      </div>

      <div className="tactile-card p-6 sm:p-8 space-y-4">
        <textarea
          data-testid="breakdown-input"
          value={prompt}
          onChange={(e)=>setPrompt(e.target.value)}
          rows={5}
          placeholder="Paste your writing prompt here..."
          className="w-full rounded-2xl border-2 border-slate-200 bg-slate-50 focus:bg-white focus:border-indigo-400 px-4 py-3 outline-none resize-none"
        />
        <button data-testid="breakdown-analyze" onClick={onAnalyze} disabled={loading}
          className="bg-indigo-600 hover:bg-indigo-500 text-white font-bold rounded-full px-6 py-3 tactile-shadow disabled:opacity-60 inline-flex items-center gap-2">
          {loading ? <><Loader2 className="animate-spin" size={16}/> Analyzing...</> : "Analyze prompt"}
        </button>
      </div>

      {result && (
        <div data-testid="breakdown-result" className="grid md:grid-cols-2 gap-6 fade-up">
          <Card title="Plain meaning" body={result.plain_meaning} color="bg-indigo-50" />
          <Card title="Question type" body={result.question_type} color="bg-amber-50" />
          <ListCard title="Task verbs (what to DO)" items={result.task_verbs} color="bg-rose-50" />
          <ListCard title="Key topics" items={result.key_topics} color="bg-sky-50" />
          <Card title="Audience" body={result.audience} color="bg-emerald-50" />
          <Card title="Format" body={result.format} color="bg-violet-50" />
          <ListCard title="Required elements" items={result.required_elements} color="bg-amber-50" />
          <ListCard title="Ask yourself first" items={result.thinking_questions} color="bg-indigo-50" />
        </div>
      )}
    </div>
  );
}

const Card = ({ title, body, color }) => (
  <div className={`rounded-3xl border-2 border-slate-100 ${color} p-6`}>
    <div className="text-xs uppercase font-bold tracking-widest text-slate-500 mb-2">{title}</div>
    <div className="text-slate-900 font-semibold">{body || "—"}</div>
  </div>
);
const ListCard = ({ title, items, color }) => (
  <div className={`rounded-3xl border-2 border-slate-100 ${color} p-6`}>
    <div className="text-xs uppercase font-bold tracking-widest text-slate-500 mb-2">{title}</div>
    <ul className="list-disc pl-5 space-y-1 text-slate-800">
      {(items || []).map((it,i)=>(<li key={i}>{typeof it === 'string' ? it : JSON.stringify(it)}</li>))}
    </ul>
  </div>
);
